/**
 * Shifts and odometer reconciliation — the screen behind the Mietwagen
 * Fahrtennachweis (HUSS 18910, see `01_Input/referenz/README.md`).
 *
 * The per-day odometer reading is a mandatory field for Mietwagenunternehmen
 * under BMF 11.03.2024 Abschnitt III, so it is captured regardless of which
 * value the app treats as the system of record for distance.
 *
 * It also does real work: comparing the odometer delta against the summed trip
 * distances is the first check an auditor performs. That matters more than
 * usual here, because the configured system of record is the routing API
 * rather than the odometer — this reconciliation is what keeps that
 * defensible.
 *
 * Breaks and the closing signature come from the paper form rather than from
 * tax law. They are kept because the paper sheet is what a driver knows, and
 * because an unexplained two-hour gap between trips invites the wrong
 * question.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Callout,
  Card,
  EmptyState,
  Field,
  SectionHeading,
  Select,
  TextInput,
} from '@/components/ui';
import {
  formatDate,
  formatDuration,
  minutesBetween,
  nowIso,
  startOfMonth,
  todayIso,
} from '@/domain/dates';
import { formatEur, formatKm, parseKmToMeters } from '@/domain/money';
import type { Shift, ShiftBreak } from '@/domain/types';
import { shiftRepo, shiftsInPeriod } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { ensureShift, reconcileShift, type ShiftReconciliation } from '@/features/trips/tripService';
import { useT } from '@/i18n';
import { de } from '@/i18n/de';

export function ShiftScreen() {
  const session = useSession();
  const t = useT();
  const [from, setFrom] = useState(startOfMonth(todayIso()));
  const [to, setTo] = useState(todayIso());
  const [rows, setRows] = useState<ShiftReconciliation[] | null>(null);

  const load = useCallback(async () => {
    const shifts = await shiftsInPeriod(session.dataKey, from, to);
    const reconciled = await Promise.all(
      shifts.map((shift) =>
        reconcileShift(session.dataKey, shift, session.app.reconciliationToleranceMeters),
      ),
    );
    setRows(reconciled.sort((a, b) => b.shift.date.localeCompare(a.shift.date)));
  }, [session.dataKey, session.app.reconciliationToleranceMeters, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  async function startToday() {
    const vehicleId = session.app.defaultVehicleId ?? session.vehicles[0]?.id;
    if (!vehicleId) return;
    await ensureShift(session.audit, {
      date: todayIso(),
      driverId: session.account.id,
      vehicleId,
    });
    await load();
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.shifts.title}
        description={t.shifts.description}
        action={
          session.vehicles.length > 0 ? (
            <button type="button" className="btn-secondary" onClick={() => void startToday()}>
              {t.shifts.startToday}
            </button>
          ) : undefined
        }
      />

      <Card>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t.common.from}>
            <TextInput type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label={t.common.to}>
            <TextInput type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
        </div>
      </Card>

      {!rows ? (
        <p className="text-muted text-sm">{t.common.loading}</p>
      ) : rows.length === 0 ? (
        <EmptyState title={t.shifts.empty}>{t.shifts.emptyBody}</EmptyState>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <ShiftCard key={row.shift.id} row={row} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShiftCard({
  row,
  onChanged,
}: {
  row: ShiftReconciliation;
  onChanged: () => Promise<void>;
}) {
  const session = useSession();
  const t = useT();
  const { shift } = row;
  const [startText, setStartText] = useState(
    shift.odometerStartMeters ? formatKm(shift.odometerStartMeters) : '',
  );
  const [endText, setEndText] = useState(
    shift.odometerEndMeters !== undefined ? formatKm(shift.odometerEndMeters) : '',
  );
  const [startedAt, setStartedAt] = useState(() => toLocalInput(shift.startedAt));
  const [endedAt, setEndedAt] = useState(() =>
    shift.endedAt ? toLocalInput(shift.endedAt) : '',
  );
  const [breaks, setBreaks] = useState<ShiftBreak[]>(shift.breaks ?? []);
  const [vehicleId, setVehicleId] = useState(shift.vehicleId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vehicle = session.vehicles.find((v) => v.id === shift.vehicleId);
  const locked = Boolean(shift.closedAt) || saving;

  async function save(patch: Partial<Shift>, reason?: string) {
    setSaving(true);
    setError(null);
    try {
      await shiftRepo.update(session.audit, shift.id, patch, reason ? { reason } : undefined);
      await onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setSaving(false);
    }
  }

  async function handleOdometerBlur() {
    const start = parseKmToMeters(startText);
    const end = parseKmToMeters(endText);
    const patch: Partial<Shift> = {};
    if (start !== null && start !== shift.odometerStartMeters) patch.odometerStartMeters = start;
    if (end !== null && end !== shift.odometerEndMeters) patch.odometerEndMeters = end;
    if (Object.keys(patch).length > 0) await save(patch);
  }

  async function handleWorkTimeBlur() {
    const patch: Partial<Shift> = {};
    const nextStart = startedAt ? new Date(startedAt).toISOString() : null;
    const nextEnd = endedAt ? new Date(endedAt).toISOString() : null;
    if (nextStart && nextStart !== shift.startedAt) patch.startedAt = nextStart;
    if (nextEnd !== (shift.endedAt ?? null)) patch.endedAt = nextEnd ?? undefined;
    if (Object.keys(patch).length > 0) await save(patch);
  }

  /** Breaks save as a whole array — a half-typed `von` without `bis` is dropped. */
  async function commitBreaks(next: ShiftBreak[]) {
    setBreaks(next);
    await save({ breaks: next.filter((pause) => pause.from && pause.to) });
  }

  async function closeShift() {
    if (row.odometerDeltaMeters === null) {
      setError(t.shifts.errOdometerEnd);
      return;
    }
    // The paper sheet is signed by the driver; the electronic equivalent is a
    // named close, timestamped in the audit chain. The reason is written in
    // German whatever the interface language is — it goes into the hash
    // preimage and is printed verbatim in every export.
    await save(
      {
        closedAt: nowIso(),
        endedAt: endedAt ? new Date(endedAt).toISOString() : nowIso(),
        signedOffBy: session.account.displayName,
      },
      de.shifts.closeReason(session.account.displayName),
    );
  }

  const missingOdometer = shift.odometerStartMeters === 0;

  return (
    <Card>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-heading text-sm font-semibold">{formatDate(shift.date)}</h3>
          <p className="text-muted text-xs">
            {vehicle
              ? `${vehicle.vehicleCode} · ${vehicle.licensePlate}`
              : t.shifts.noVehicle}{' '}
            · {t.shifts.tripCount(row.tripCount)}
            {shift.endedAt && ` · ${formatDuration(minutesBetween(shift.startedAt, shift.endedAt))}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {shift.closedAt ? (
            <Badge tone="success">{t.shifts.closed}</Badge>
          ) : (
            <Badge tone="warning">{t.shifts.open}</Badge>
          )}
        </div>
      </header>

      {missingOdometer && <Callout tone="warning">{t.shifts.odometerMissing}</Callout>}

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Field label={t.shifts.workStart}>
          <TextInput
            type="datetime-local"
            value={startedAt}
            onChange={(event) => setStartedAt(event.target.value)}
            onBlur={() => void handleWorkTimeBlur()}
            disabled={locked}
          />
        </Field>
        <Field label={t.shifts.workEnd}>
          <TextInput
            type="datetime-local"
            value={endedAt}
            onChange={(event) => setEndedAt(event.target.value)}
            onBlur={() => void handleWorkTimeBlur()}
            disabled={locked}
          />
        </Field>
        <Field label={t.shifts.vehicle}>
          <Select
            value={vehicleId}
            onChange={(event) => {
              setVehicleId(event.target.value);
              void save({ vehicleId: event.target.value });
            }}
            disabled={locked}
          >
            {session.vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.vehicleCode} · {v.licensePlate}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.shifts.odometerStart}>
          <TextInput
            value={startText}
            onChange={(event) => setStartText(event.target.value)}
            onBlur={() => void handleOdometerBlur()}
            inputMode="decimal"
            disabled={locked}
            placeholder="142350,0"
          />
        </Field>
        <Field label={t.shifts.odometerEnd}>
          <TextInput
            value={endText}
            onChange={(event) => setEndText(event.target.value)}
            onBlur={() => void handleOdometerBlur()}
            inputMode="decimal"
            disabled={locked}
            placeholder="142612,4"
          />
        </Field>
      </div>

      <div className="mt-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="field-label mb-0">{t.shifts.breaks}</span>
          {!locked && (
            <button
              type="button"
              className="text-accent text-xs hover:underline"
              onClick={() => setBreaks([...breaks, { from: '', to: '' }])}
            >
              {t.shifts.addBreak}
            </button>
          )}
        </div>

        {breaks.length === 0 ? (
          <p className="text-muted text-xs">{t.shifts.noBreaks}</p>
        ) : (
          <ul className="space-y-2">
            {breaks.map((pause, index) => (
              <li key={index} className="flex items-center gap-2">
                <TextInput
                  type="time"
                  aria-label={t.shifts.breakFrom(index + 1)}
                  value={pause.from}
                  onChange={(event) =>
                    setBreaks(
                      breaks.map((entry, position) =>
                        position === index ? { ...entry, from: event.target.value } : entry,
                      ),
                    )
                  }
                  onBlur={() => void commitBreaks(breaks)}
                  disabled={locked}
                  className="w-32"
                />
                <span className="text-muted text-xs">{t.shifts.until}</span>
                <TextInput
                  type="time"
                  aria-label={t.shifts.breakTo(index + 1)}
                  value={pause.to}
                  onChange={(event) =>
                    setBreaks(
                      breaks.map((entry, position) =>
                        position === index ? { ...entry, to: event.target.value } : entry,
                      ),
                    )
                  }
                  onBlur={() => void commitBreaks(breaks)}
                  disabled={locked}
                  className="w-32"
                />
                {!locked && (
                  <button
                    type="button"
                    className="text-muted text-xs hover:text-red-600"
                    onClick={() =>
                      void commitBreaks(breaks.filter((_, position) => position !== index))
                    }
                  >
                    {t.shifts.remove}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-subtle mt-4 rounded-xl p-3">
        <h4 className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">
          {t.shifts.reconciliation}
        </h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
          <Metric term={t.shifts.sumTrips} value={`${formatKm(row.tripDistanceMeters)} km`} />
          <Metric
            term={t.shifts.perOdometer}
            value={
              row.odometerDeltaMeters === null
                ? t.common.dash
                : `${formatKm(row.odometerDeltaMeters)} km`
            }
          />
          <Metric
            term={t.shifts.difference}
            value={
              row.differenceMeters === null
                ? t.common.dash
                : `${formatKm(row.differenceMeters)} km`
            }
            tone={
              row.withinTolerance === null
                ? undefined
                : row.withinTolerance
                  ? 'text-emerald-700'
                  : 'text-amber-700'
            }
          />
          <Metric term={t.shifts.revenue} value={formatEur(row.grossRevenueCents)} />
          <Metric
            term={t.shifts.breaks}
            value={row.breakMinutes > 0 ? formatDuration(row.breakMinutes) : t.common.dash}
          />
          <Metric
            term={t.shifts.workingTime}
            value={
              row.workingMinutes === null ? t.common.dash : formatDuration(row.workingMinutes)
            }
          />
        </dl>

        {row.withinTolerance === false && row.differenceMeters !== null && (
          <p className="mt-2 text-xs text-amber-700">
            {row.differenceMeters < 0 ? t.shifts.tripsLonger : t.shifts.odometerLonger}
          </p>
        )}

        {Object.keys(row.revenueByPaymentMethod).length > 0 && (
          <p className="text-muted mt-2 text-xs">
            {t.shifts.byPaymentMethod}{' '}
            {Object.entries(row.revenueByPaymentMethod)
              .map(
                ([method, cents]) =>
                  `${t.labels.payment[method as keyof typeof t.labels.payment] ?? method} ${formatEur(cents)}`,
              )
              .join(' · ')}
            {row.tipCents > 0 && t.shifts.tipTotal(formatEur(row.tipCents))}
          </p>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}

      {shift.closedAt ? (
        <p className="text-muted mt-3 text-xs">
          {t.shifts.confirmedBy(
            shift.signedOffBy ?? session.account.displayName,
            formatDate(shift.closedAt.slice(0, 10)),
          )}
        </p>
      ) : (
        <button
          type="button"
          className="btn-secondary mt-3"
          onClick={() => void closeShift()}
          disabled={saving}
        >
          {t.shifts.close}
        </button>
      )}
    </Card>
  );
}

/** `datetime-local` wants local time without a zone suffix. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function Metric({ term, value, tone }: { term: string; value: string; tone?: string }) {
  return (
    <div>
      <dt className="text-muted">{term}</dt>
      <dd className={`font-semibold ${tone ?? 'text-heading'}`}>{value}</dd>
    </div>
  );
}
